import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLock } from '../context/LockContext';
import Icon from './Icon';
import './ChangePinModal.css';

function ChangePinModal({ onClose }) {
  const { t } = useTranslation();
  const { changePin } = useLock();

  // Close modal on Escape key (WCAG 2.1.2)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  const [step, setStep] = useState('current'); // 'current', 'new', 'confirm'
  const [currentPin, setCurrentPin] = useState('');
  const [newPin, setNewPin] = useState('');
  const [confirmPin, setConfirmPin] = useState('');
  const [error, setError] = useState('');
  const [shake, setShake] = useState(false);

  const getCurrentPin = () => {
    if (step === 'current') return currentPin;
    if (step === 'new') return newPin;
    if (step === 'confirm') return confirmPin;
    return '';
  };

  const handleNumberClick = (num) => {
    if (error) setError('');
    if (shake) setShake(false);

    const currentValue = getCurrentPin();
    if (currentValue.length >= 6) return;

    const newValue = currentValue + num;

    if (step === 'current') {
      setCurrentPin(newValue);
    } else if (step === 'new') {
      setNewPin(newValue);
    } else if (step === 'confirm') {
      setConfirmPin(newValue);
    }
  };

  const handleChangePinSubmit = (current, newP, confirm) => {
    try {
      if (newP !== confirm) {
        setError(t('lock.pinMismatch'));
        setShake(true);
        setTimeout(() => {
          setNewPin('');
          setConfirmPin('');
          setStep('new');
          setShake(false);
        }, 1000);
        return;
      }

      changePin(current, newP);

      // Éxito
      setError('');
      alert(t('lock.pinChangedSuccess'));
      onClose();
    } catch (err) {
      setError(
        err.code === 'INCORRECT_CURRENT_PIN' ? t('lock.incorrectCurrentPin') : t('lock.changeError')
      );
      setShake(true);
      setTimeout(() => {
        setCurrentPin('');
        setNewPin('');
        setConfirmPin('');
        setStep('current');
        setShake(false);
      }, 1500);
    }
  };

  const handleStepSubmit = () => {
    const currentValue = getCurrentPin();
    if (currentValue.length < 4) return;

    if (step === 'current') {
      setStep('new');
    } else if (step === 'new') {
      setStep('confirm');
    } else if (step === 'confirm') {
      handleChangePinSubmit(currentPin, newPin, confirmPin);
    }
  };

  const handleBackspace = () => {
    if (error) setError('');
    if (shake) setShake(false);

    if (step === 'current') {
      setCurrentPin(currentPin.slice(0, -1));
    } else if (step === 'new') {
      setNewPin(newPin.slice(0, -1));
    } else if (step === 'confirm') {
      setConfirmPin(confirmPin.slice(0, -1));
    }
  };

  const handleBack = () => {
    setError('');
    setShake(false);

    if (step === 'confirm') {
      setConfirmPin('');
      setStep('new');
    } else if (step === 'new') {
      setNewPin('');
      setStep('current');
    }
  };

  const currentValue = getCurrentPin();

  const getTitle = () => {
    if (step === 'current') return t('lock.enterCurrentPin');
    if (step === 'new') return t('lock.enterNewPin');
    if (step === 'confirm') return t('lock.confirmNewPin');
    return '';
  };

  return (
    <>
      <div className="modal-overlay" onClick={onClose}></div>
      <div className="change-pin-modal" role="dialog" aria-modal="true">
        <div className="modal-header">
          <h2>{t('lock.changePin')}</h2>
          <button type="button" className="modal-close-btn" onClick={onClose}>
            <Icon name="close" size="small" />
          </button>
        </div>

        <div className="modal-body">
          <div className="pin-step-indicator">
            <div
              className={`step ${step === 'current' ? 'active' : step !== 'current' ? 'completed' : ''}`}
            >
              1
            </div>
            <div className="step-line"></div>
            <div
              className={`step ${step === 'new' ? 'active' : step === 'confirm' ? 'completed' : ''}`}
            >
              2
            </div>
            <div className="step-line"></div>
            <div className={`step ${step === 'confirm' ? 'active' : ''}`}>3</div>
          </div>

          <h3 className="pin-title">{getTitle()}</h3>

          {error && <div className="pin-error">{error}</div>}

          <div className={`pin-dots ${shake ? 'shake' : ''}`}>
            {[0, 1, 2, 3, 4, 5].map((index) => (
              <div
                key={index}
                className={`pin-dot ${currentValue.length > index ? 'filled' : ''} ${index >= 4 ? 'optional' : ''}`}
              />
            ))}
          </div>

          <div className="pin-keypad">
            {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((num) => (
              <button
                type="button"
                key={num}
                className="pin-button"
                onClick={() => handleNumberClick(num.toString())}
              >
                {num}
              </button>
            ))}
            {step !== 'current' ? (
              <button type="button" className="pin-button back-button" onClick={handleBack}>
                <Icon name="arrow_back" size="small" />
              </button>
            ) : (
              <button type="button" className="pin-button empty" disabled></button>
            )}
            <button type="button" className="pin-button" onClick={() => handleNumberClick('0')}>
              0
            </button>
            <button type="button" className="pin-button backspace" onClick={handleBackspace}>
              <Icon name="backspace" size="small" />
            </button>
          </div>

          <button
            type="button"
            className="btn btn-primary pin-submit-btn"
            onClick={handleStepSubmit}
            disabled={getCurrentPin().length < 4}
          >
            {step === 'confirm' ? t('common.confirm') : t('lock.pinNext')}
          </button>
        </div>
      </div>
    </>
  );
}

export default ChangePinModal;
